import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";
import moment from "moment";
import { CurrencyFormatter } from "../../Components/CurrencyFormatter";
import * as api from "../../Service/api";
import "../../Styles/components/tags.scss";

const formatDetailDate = (date) => {
  if (!date || !moment(date).isValid()) return null;
  return moment(date).format("MMM DD, YYYY");
};

const toPlain = (data) => {
  if (data == null) return data;
  if (typeof data.asMutable === "function") return data.asMutable({ deep: true });
  if (typeof data.toJS === "function") return data.toJS();
  return data;
};

const normalizeTags = (tags) => {
  const plain = toPlain(tags);
  if (!plain) return [];
  if (Array.isArray(plain)) {
    return plain
      .map((tag) => {
        if (typeof tag === "string") return tag;
        return tag?.name ?? tag?.label ?? null;
      })
      .filter(Boolean);
  }
  if (typeof plain === "string") {
    return plain
      .replace(/[{}]/g, "")
      .split(/[,|/]/)
      .map((tag) => tag.replace(/"/g, "").trim())
      .filter(Boolean);
  }
  return [];
};

const getInitial = (name) => {
  if (!name || name === null) return "?";
  return name.trim().charAt(0).toUpperCase();
};

const DetailRow = ({ label, value, children }) => {
  if (children == null && (value == null || value === "" || value === null)) {
    return null;
  }

  return (
    <div className="primary-customer-modal__detail-row">
      <span className="primary-customer-modal__detail-label">{label}</span>
      {children ?? (
        <span className="primary-customer-modal__detail-value">{value}</span>
      )}
    </div>
  );
};

const StatItem = ({ label, value, variant }) => {
  if (value == null || value === "") return null;
  return (
    <div
      className={`primary-customer-modal__stat${
        variant ? ` primary-customer-modal__stat--${variant}` : ""
      }`}
    >
      <span className="primary-customer-modal__stat-label">{label}</span>
      <span className="primary-customer-modal__stat-value">{value}</span>
    </div>
  );
};

function PrimaryBusinessCustomerModal({
  visible,
  onHide,
  secondaryCustomer,
  primaryCustomerId,
  currencySymbol = "",
  currencyFormat = "%s %v",
}) {
  const { t } = useTranslation("common");
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(false);
  const [primarySummary, setPrimarySummary] = useState(null);

  useEffect(() => {
    if (!visible || !primaryCustomerId) {
      setPrimarySummary(null);
      setError(false);
      setFetching(false);
      return;
    }

    let cancelled = false;
    const loadPrimary = async () => {
      setFetching(true);
      setError(false);
      const response = await api.getCustomerSummary(primaryCustomerId);
      if (cancelled) return;
      if (response.ok) {
        setPrimarySummary(response.data);
        setError(false);
      } else {
        setPrimarySummary(null);
        setError(true);
      }
      setFetching(false);
    };

    loadPrimary();
    return () => {
      cancelled = true;
    };
  }, [visible, primaryCustomerId]);

  const details = useMemo(() => {
    const summary = toPlain(primarySummary);
    if (!summary) return null;

    const bc = summary.businessCustomer || {};
    const lastPayment = summary.lastPayment;
    const recentSub = summary.recentSub;

    const formatMoney = (amount) => {
      if (amount == null) return null;
      return CurrencyFormatter({
        amount,
        currencySymbol,
        currencyFormat,
        as: "string",
      });
    };

    return {
      id: summary.id ?? bc.id,
      name: bc.name,
      customerNum: bc.customerNum,
      phoneNo: bc.phoneNo ?? bc.phoneNoMasked,
      email: bc.email,
      reference: bc.reference,
      segment: bc.segment?.name,
      addedOn: formatDetailDate(summary.addedOn ?? bc.addedOn),
      collected: formatMoney(summary.collected),
      revenue: formatMoney(summary.revenue),
      collectedCount: summary.collectedCount,
      missedCount: summary.missedCount,
      pendingCount: summary.pendingCount,
      activeSubs: summary.activeSubs,
      completedSubs: summary.completedSubs,
      cancelledSubs: summary.cancelledSubs,
      pausedSubs: summary.pausedSubs,
      recentSubStatus: recentSub?.status,
      recentSubPlan: recentSub?.plan?.name,
      lastPaymentAmount: formatMoney(lastPayment?.amount),
      lastPaymentDate: formatDetailDate(lastPayment?.receivedAt),
      tags: normalizeTags(summary.tags ?? bc.tags),
    };
  }, [primarySummary, currencySymbol, currencyFormat]);

  const secondaryDisplayName =
    secondaryCustomer?.name && secondaryCustomer.name !== null
      ? secondaryCustomer.name
      : secondaryCustomer?.bcId;

  const secondaryTags = useMemo(
    () => normalizeTags(secondaryCustomer?.tags),
    [secondaryCustomer?.tags]
  );

  const footer = (
    <div className="flex justify-content-end">
      <Button label={t("common.cancel")} className="p-button-outlined" onClick={onHide} />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={t("business_customers.primary_customer_title")}
      style={{ width: "720px", maxWidth: "95vw" }}
      contentStyle={{ padding: "1.5rem 1.75rem" }}
      footer={footer}
      modal
      className="primary-customer-modal"
    >
      {secondaryCustomer && (
        <div className="primary-customer-modal__secondary-banner mb-3">
          <i className="pi pi-link primary-customer-modal__secondary-icon" />
          <div>
            <div className="primary-customer-modal__secondary-label">
              {t("business_customers.linked_secondary")}
            </div>
            <div className="primary-customer-modal__secondary-name">
              {secondaryDisplayName}
            </div>
            {secondaryCustomer.phoneNo && secondaryCustomer.phoneNo !== null && (
              <div className="primary-customer-modal__secondary-meta">
                {t("common.number")}: {secondaryCustomer.phoneNo}
              </div>
            )}
            {(secondaryCustomer.segment || secondaryTags.length > 0) && (
              <div className="primary-customer-modal__secondary-segment-tags mt-2">
                {secondaryCustomer.segment && (
                  <div className="segment-tag-div mb-2">
                    <span className="segment-class">{secondaryCustomer.segment}</span>
                  </div>
                )}
                {secondaryTags.length > 0 && (
                  <div className="tags-container">
                    {secondaryTags.map((tag) => (
                      <div key={tag} className="tag-div-main">
                        <span className="tag-class">{tag}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {fetching && (
        <div className="primary-customer-modal__loading">
          <ProgressSpinner style={{ width: "32px", height: "32px" }} strokeWidth="4" />
        </div>
      )}

      {!fetching && error && (
        <Message
          severity="error"
          text={t("business_customers.primary_customer_error")}
          className="w-full"
        />
      )}

      {!fetching && !error && details && (
        <>
          <div className="primary-customer-modal__hero mt-4 mb-5">
            <div className="primary-customer-modal__avatar">{getInitial(details.name)}</div>
            <div className="primary-customer-modal__hero-content">
              <span className="customer-link-badge customer-link-badge--primary primary-customer-modal__hero-badge">
                {t("business_customers.primary")}
              </span>
              <div className="primary-customer-modal__hero-name">{details.name || null}</div>
              <div className="primary-customer-modal__hero-meta">
                {details.id != null && (
                  <span className="primary-customer-modal__hero-id">
                    {t("common.id")}: {details.id}
                  </span>
                )}
                {details.customerNum && (
                  <span className="primary-customer-modal__hero-id">
                    {t("business_customers.customer_id")}: {details.customerNum}
                  </span>
                )}
              </div>
              {(details.segment || details.tags.length > 0) && (
                <div className="primary-customer-modal__hero-segment-tags mt-2">
                  {details.segment && (
                    <div className="segment-tag-div mb-2">
                      <span className="segment-class">{details.segment}</span>
                    </div>
                  )}
                  {details.tags.length > 0 && (
                    <div className="tags-container">
                      {details.tags.map((tag) => (
                        <div key={tag} className="tag-div-main">
                          <span className="tag-class">{tag}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="grid">
            <div className="col-12">
              <div className="primary-customer-modal__section">
                <div className="primary-customer-modal__section-title">
                  {t("business_customers.primary_customer_profile")}
                </div>
                <DetailRow label={t("common.number")} value={details.phoneNo} />
                <DetailRow label={t("common.email")} value={details.email} />
                <DetailRow
                  label={t("business_customers.customer_ref")}
                  value={details.reference}
                />
                <DetailRow label={t("business_customers.joined_on")} value={details.addedOn} />
              </div>
            </div>

            <div className="col-12">
              <div className="primary-customer-modal__section primary-customer-modal__section--activity">
                <div className="primary-customer-modal__section-title">
                  {t("business_customers.primary_customer_activity")}
                </div>

                {(details.collected || details.revenue) && (
                  <div className="primary-customer-modal__highlights">
                    {details.collected && (
                      <div className="primary-customer-modal__highlight primary-customer-modal__highlight--collected">
                        <span className="primary-customer-modal__highlight-label">
                          {t("business_customers.paid")}
                        </span>
                        <span className="primary-customer-modal__highlight-value">
                          {details.collected}
                        </span>
                      </div>
                    )}
                    {details.revenue && (
                      <div className="primary-customer-modal__highlight primary-customer-modal__highlight--revenue">
                        <span className="primary-customer-modal__highlight-label">
                          {t("business_customers.revenue")}
                        </span>
                        <span className="primary-customer-modal__highlight-value">
                          {details.revenue}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <div className="primary-customer-modal__subsection-title">
                  {t("business_customers.collections")}
                </div>
                <div className="primary-customer-modal__stat-grid">
                  <StatItem
                    label={t("business_customers.collected_count")}
                    value={details.collectedCount}
                    variant="collected"
                  />
                  <StatItem
                    label={t("business_customers.missed_count")}
                    value={details.missedCount}
                    variant="missed"
                  />
                  <StatItem
                    label={t("business_customers.pending_count")}
                    value={details.pendingCount}
                    variant="pending"
                  />
                </div>

                <div className="primary-customer-modal__subsection-title">
                  {t("business_customers.subscriptions_summary")}
                </div>
                <div className="primary-customer-modal__stat-grid">
                  <StatItem
                    label={t("business_customers.active_subs")}
                    value={details.activeSubs}
                  />
                  <StatItem
                    label={t("business_customers.completed_subs")}
                    value={details.completedSubs}
                  />
                  <StatItem
                    label={t("business_customers.cancelled_subs")}
                    value={details.cancelledSubs}
                  />
                  <StatItem
                    label={t("business_customers.paused_subs")}
                    value={details.pausedSubs}
                  />
                </div>

                {details.lastPaymentAmount && (
                  <div className="primary-customer-modal__footnote">
                    <div className="primary-customer-modal__footnote-label">
                      {t("business_customers.last_payment")}
                    </div>
                    <div className="primary-customer-modal__footnote-value">
                      {details.lastPaymentAmount}
                      {details.lastPaymentDate ? ` · ${details.lastPaymentDate}` : ""}
                    </div>
                  </div>
                )}

                {details.recentSubStatus && (
                  <div className="primary-customer-modal__footnote">
                    <div className="primary-customer-modal__footnote-label">
                      {t("business_customers.recent_subscription")}
                    </div>
                    <div className="primary-customer-modal__footnote-value primary-customer-modal__recent-sub">
                      <span
                        className={`status-outline status-${details.recentSubStatus.toLowerCase()} status-content-stat`}
                      >
                        {details.recentSubStatus === "COMPLETE"
                          ? t("business_customers.completed")
                          : details.recentSubStatus}
                      </span>
                      {details.recentSubPlan && (
                        <span className="primary-customer-modal__recent-sub-plan">
                          {details.recentSubPlan}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </Dialog>
  );
}

export default PrimaryBusinessCustomerModal;
